import { STATUS } from "@/constants/statuses";
import RewardsVipTiersApiService from "@apps/rewards/modules/VipTiers/api_service/RewardsVipTiersApiService";
import { normalizeLocale } from "@apps/rewards/utils/normalizeLocale/normalizeLocale";
import { getLoadingStatus, isVirginStatus } from "@apps/rewards/utils/statuses";
import { applyTransaction } from "@datorama/akita";
import type { LocalizationInfoProviderInterface } from "@lib/LocalizationInfoProvider/LocalizationInfoProviderInterface";
import type { Observable, Subscription } from "rxjs";
import {
    catchError,
    combineLatestWith,
    filter,
    lastValueFrom,
    tap,
} from "rxjs";
import { inject, singleton } from "tsyringe";

import type { LoggerInterface } from "@interfaces/LoggerInterface";

import { rewardsApplicationLoggerToken } from "../../../tokens";
import type {
    VipTierProgressResponse,
    VipTiersResponse,
} from "../entities/RewardsVipTiersEntities";
import RewardsVipTierProgressQuery from "../queries/RewardsVipTierProgressQuery";
import RewardsVipTiersQuery from "../queries/RewardsVipTiersQuery";
import RewardsVipTierProgressStore from "../stores/RewardsVipTierProgressStore";
import RewardsVipTiersStore from "../stores/RewardsVipTiersStore";
import type { VipTiersQueryParams } from "../types";

@singleton()
class RewardsVipTiersService {
    constructor(
        @inject(rewardsApplicationLoggerToken)
        private readonly logger: LoggerInterface,
        private readonly vipTiersApiService: RewardsVipTiersApiService,
        private readonly vipTiersStore: RewardsVipTiersStore,
        private readonly vipTiersQuery: RewardsVipTiersQuery,
        private readonly vipTierProgressStore: RewardsVipTierProgressStore,
        private readonly vipTierProgressQuery: RewardsVipTierProgressQuery
    ) {}

    public getTiers(
        queryParams: VipTiersQueryParams
    ): Observable<VipTiersResponse> {
        this.logger.debug("RewardsVipTiersService.getTiers is started");
        this.vipTiersStore.setStatus(
            getLoadingStatus(this.vipTiersStore.getStatus())
        );

        return this.vipTiersApiService.getTiers(queryParams).pipe(
            catchError((error: unknown) => {
                this.logger.debug(
                    "RewardsVipTiersService.getTiers is errored",
                    error
                );
                this.vipTiersStore.setStatus(STATUS.ERROR);
                throw error;
            }),
            tap((value) => {
                this.logger.debug("RewardsVipTiersService.getTiers is ready");

                //Store Update
                applyTransaction(() => {
                    this.vipTiersStore.update((state) => ({
                        ...state,
                        tierList: value.tiers,
                    }));
                    this.vipTiersStore.setStatus(STATUS.READY);
                });
            })
        );
    }

    public getTierProgress(): Observable<VipTierProgressResponse> {
        this.logger.debug("RewardsVipTiersService.getTierProgress is started");
        this.vipTierProgressStore.setStatus(
            getLoadingStatus(this.vipTierProgressStore.getStatus())
        );

        return this.vipTiersApiService.getTierProgress().pipe(
            catchError((error: unknown) => {
                this.logger.debug(
                    "RewardsVipTiersService.getTierProgress is errored",
                    error
                );
                this.vipTierProgressStore.setStatus(STATUS.ERROR);
                throw error;
            }),
            tap((value) => {
                this.logger.debug(
                    "RewardsVipTiersService.getTierProgress is ready"
                );

                //Store Update
                applyTransaction(() => {
                    this.vipTierProgressStore.update((state) => ({
                        ...state,
                        tierProgress: value.progress,
                    }));
                    this.vipTierProgressStore.setStatus(STATUS.READY);
                });
            })
        );
    }

    public getTiersAndProgress = ({
        programsStatus$,
        isCustomer$,
        localizationInfoProvider,
    }: {
        programsStatus$: Observable<boolean>;
        isCustomer$: Observable<boolean>;
        localizationInfoProvider: LocalizationInfoProviderInterface;
    }): Subscription => {
        return programsStatus$
            .pipe(
                filter(Boolean),
                combineLatestWith(this.vipTiersQuery.status$),
                tap(([, status]) => {
                    if (!isVirginStatus(status)) return;

                    void lastValueFrom(
                        this.getTiers({
                            locale: normalizeLocale(
                                localizationInfoProvider.getLanguageIsoCode()
                            ),
                        })
                    );
                }),
                combineLatestWith(
                    isCustomer$,
                    this.vipTierProgressQuery.status$
                ),
                filter(
                    ([, isCustomer, status]) =>
                        isCustomer && isVirginStatus(status)
                ),
                tap(() => void lastValueFrom(this.getTierProgress()))
            )
            .subscribe();
    };
}

export default RewardsVipTiersService;
